<template>
    <div class="sl-quota-details" :class="{ 'sl-quota-details--link': variant === 'link' }" ref="root">
        <button
            ref="trigger"
            type="button"
            class="sl-quota-details__trigger"
            :aria-expanded="open ? 'true' : 'false'"
            t_label="Emp_saleslead_quota_details_toggle"
            @click.stop="toggle"
        >
            <span class="sl-quota-details__trigger-text">{{ $t('lbl_EMPBE_SalesLead_QuotaDetails') }}</span>
            <span class="sl-quota-details__trigger-affix" aria-hidden="true">
                <!-- default：info；link：向下 chevron -->
                <svg
                    v-if="variant !== 'link'"
                    class="sl-quota-details__trigger-icon"
                    width="15"
                    height="15"
                    viewBox="0 0 15 15"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                >
                    <path
                        d="M7.5 0C3.36 0 0 3.36 0 7.5C0 11.64 3.36 15 7.5 15C11.64 15 15 11.64 15 7.5C15 3.36 11.64 0 7.5 0ZM8.25 11.25H6.75V6.75H8.25V11.25ZM8.25 5.25H6.75V3.75H8.25V5.25Z"
                        fill="black"
                        fill-opacity="0.4"
                    />
                </svg>
                <svg
                    v-else
                    class="sl-quota-details__trigger-chevron sl-quota-details__trigger-chevron--down"
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 20 20"
                    fill="none"
                >
                    <path d="M6.175 12.8417L10 9.025L13.825 12.8417L15 11.6667L10 6.66669L5 11.6667L6.175 12.8417Z" fill="currentColor"/>
                </svg>
            </span>
        </button>

        <!-- 挂到 body，避免被左侧白卡片叠层盖住 -->
        <div
            v-show="open"
            ref="popover"
            class="sl-quota-popover"
            :style="popoverStyle"
            @click.stop
        >
            <div class="sl-quota-popover__top">
                <div class="sl-quota-popover__row">
                    <span class="sl-quota-popover__title">{{ $t('lbl_EMPBE_SalesLead_TotalQuotas') }}</span>
                    <span v-if="isUnlimited" class="sl-quota-popover__nums sl-quota-popover__nums--unlimited">
                        {{ $t('lbl_EMPBE_SalesLead_Unlimited') }}
                    </span>
                    <span v-else class="sl-quota-popover__nums">
                        <strong>{{ totalRemaining }}</strong>
                        <span class="sl-quota-popover__nums-sub">{{ $t('lbl_EMPBE_SalesLead_QuotaLeftWord') }} / {{ totalLimit }}</span>
                    </span>
                </div>
                <p class="sl-quota-popover__desc" v-html="quotaDescHtml" @click="onDescClick"></p>
            </div>

            <ul class="sl-quota-popover__list" v-if="rows.length">
                <li v-for="row in rows" :key="row.type" class="sl-quota-popover__item">
                    <img class="sl-quota-popover__item-icon" :src="row.icon" alt="" width="16" height="16" />
                    <span class="sl-quota-popover__item-label">{{ row.label }}</span>
                    <span
                        class="sl-quota-popover__item-nums"
                        :class="{ 'sl-quota-popover__item-nums--unlimited': isUnlimited }"
                    >
                        <template v-if="isUnlimited">{{ $t('lbl_EMPBE_SalesLead_Unlimited') }}</template>
                        <template v-else>{{ row.remaining }} / {{ row.limit }}</template>
                    </span>
                </li>
            </ul>

            <div class="sl-quota-popover__upgrade" v-if="showUpgrade && !isUnlimited">
                <div class="sl-quota-popover__upgrade-text">
                    <div class="sl-quota-popover__upgrade-title">{{ $t('lbl_EMPBE_SalesLead_UpgradePlanTitle') }}</div>
                    <p class="sl-quota-popover__upgrade-desc">{{ $t('lbl_EMPBE_SalesLead_UpgradePlanDesc') }}</p>
                </div>
                <div class="sl-quota-popover__upgrade-actions">
                    <a
                        class="sl-quota-popover__btn-learn"
                        :href="learnMoreUrl"
                        target="_blank"
                        rel="noopener"
                        t_label="Emp_saleslead_quota_learn_more"
                        @click.stop
                    >{{ $t('lbl_EMPBE_SalesLead_LearnMore') }}</a>
                    <a
                        href="javascript:void(0)"
                        class="sl-quota-popover__btn-sales"
                        t_label="Emp_saleslead_quota_contact_sales"
                        @click.stop.prevent="onContactSales"
                    >{{ $t('lbl_EMPBE_ContactSales') }}</a>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { InquiryFormService } from '@/_services';

const SL_NEWICON_CDN = 'https://cdn.adsalecdn.com/EMP/2-0/SalesLead/Newicon/';
const POPOVER_WIDTH = 300;
const POPOVER_GAP = 8;

function pickYearQuota(res) {
    const year = res?.yearUnlockQuota || res?.YearUnlockQuota || res || {};
    const remaining = Number(year.yearRemainingQuota ?? year.YearRemainingQuota ?? 0);
    const limit = Number(year.everyYearQuota ?? year.EveryYearQuota ?? 0);
    return {
        remaining: Number.isFinite(remaining) ? remaining : 0,
        limit: Number.isFinite(limit) ? limit : 0,
    };
}

export default {
    name: 'SalesLeadQuotaDetails',
    props: {
        companyId: {
            type: [Number, String],
            default: 0,
        },
        showUpgrade: {
            type: Boolean,
            default: true,
        },
        /** 付费档等不需额度时，直接按无限展示 */
        unlimited: {
            type: Boolean,
            default: false,
        },
        /** default=等级条 ⓘ（开合不变）；link=解锁条紫色链接+向下 chevron */
        variant: {
            type: String,
            default: 'default',
        },
    },
    data() {
        return {
            open: false,
            rows: [],
            loadedUnlimited: false,
            popoverStyle: {
                top: '0px',
                left: '0px',
                width: `${POPOVER_WIDTH}px`,
            },
        };
    },
    computed: {
        langCode() {
            return localStorage.getItem('currentLang') || 'simp';
        },
        /** 与采购墙「了解更多」一致：会员级别及权益页 */
        learnMoreUrl() {
            const lang = this.langCode || 'simp';
            const base = (process.env.VUE_APP_EMP_VIEW || 'https://www.chinaplasonline.com/eMarketplace/').replace(/\/?$/, '/');
            return `${base}MembershipsAndBenefits/${lang}`;
        },
        totalRemaining() {
            return this.rows.reduce((sum, r) => sum + (Number(r.remaining) || 0), 0);
        },
        totalLimit() {
            return this.rows.reduce((sum, r) => sum + (Number(r.limit) || 0), 0);
        },
        isUnlimited() {
            return this.unlimited || this.loadedUnlimited;
        },
        sourcingWallHref() {
            return this.$router.resolve({ path: '/BuyerRequest/default' }).href;
        },
        quotaDescHtml() {
            const key = this.isUnlimited
                ? 'lbl_EMPBE_SalesLead_QuotaDetailsDescUnlimited'
                : 'lbl_EMPBE_SalesLead_QuotaDetailsDesc';
            return this.$t(key, { SourcingWall: this.sourcingWallHref });
        },
        typeDefs() {
            return [
                {
                    type: 'Enquiry',
                    label: this.$t('lbl_EMPBE_SalesLead_NameCardPage_EnquiriesTab'),
                    icon: `${SL_NEWICON_CDN}Enquiry.svg`,
                },
                {
                    type: 'NameCard',
                    label: this.$t('lbl_EMPBE_SalesLead_NameCardPage_NameCardTab'),
                    icon: `${SL_NEWICON_CDN}id_card_small.svg`,
                },
                {
                    type: 'Bookmark',
                    label: this.$t('lbl_EMPBE_SalesLead_LikeFollow'),
                    icon: `${SL_NEWICON_CDN}favorite.svg`,
                },
            ];
        },
    },
    mounted() {
        document.addEventListener('click', this.onDocumentClick, true);
        window.addEventListener('resize', this.onViewportChange);
        window.addEventListener('scroll', this.onViewportChange, true);
        this.$nextTick(() => {
            this.mountPopoverToBody();
        });
    },
    beforeDestroy() {
        document.removeEventListener('click', this.onDocumentClick, true);
        window.removeEventListener('resize', this.onViewportChange);
        window.removeEventListener('scroll', this.onViewportChange, true);
        this.unmountPopoverFromBody();
    },
    methods: {
        mountPopoverToBody() {
            const el = this.$refs.popover;
            if (el && el.parentNode !== document.body) {
                document.body.appendChild(el);
            }
        },
        unmountPopoverFromBody() {
            const el = this.$refs.popover;
            if (el && el.parentNode === document.body) {
                document.body.removeChild(el);
            }
        },
        toggle() {
            if (this.open) {
                this.close();
                return;
            }
            this.open = true;
            this.mountPopoverToBody();
            this.$nextTick(() => {
                this.updatePopoverPosition();
            });
            this.loadQuotas();
        },
        close() {
            this.open = false;
        },
        onContactSales() {
            this.close();
            this.$emit('contact-sales');
        },
        onDescClick(e) {
            const link = e.target && e.target.closest
                ? e.target.closest('a.sl-quota-popover__wall-link')
                : null;
            if (!link) return;
            e.preventDefault();
            e.stopPropagation();
            this.close();
            this.$router.push({ path: '/BuyerRequest/default' }).catch(() => {});
        },
        onDocumentClick(e) {
            if (!this.open) return;
            const trigger = this.$refs.trigger;
            const popover = this.$refs.popover;
            if (trigger && trigger.contains(e.target)) return;
            if (popover && popover.contains(e.target)) return;
            this.close();
        },
        onViewportChange() {
            if (!this.open) return;
            this.updatePopoverPosition();
        },
        updatePopoverPosition() {
            const trigger = this.$refs.trigger;
            if (!trigger) return;
            const rect = trigger.getBoundingClientRect();
            // 右缘贴齐左侧面板（leftmenu-shell），避免 plan-bar 内边距导致弹层缩进
            const root = this.$refs.root;
            const panel = root
                && (root.closest('.leftmenu-shell')
                    || root.closest('.sales-lead-left')
                    || root.closest('.sl-plan-bar'));
            const edgeRect = (panel && panel.getBoundingClientRect()) || rect;
            const width = Math.min(POPOVER_WIDTH, window.innerWidth - 16);
            let left = edgeRect.right - width;
            if (left < 8) left = 8;
            if (left + width > window.innerWidth - 8) {
                left = Math.max(8, window.innerWidth - width - 8);
            }
            let top = rect.bottom + POPOVER_GAP;
            const popover = this.$refs.popover;
            const estimatedHeight = (popover && popover.offsetHeight) || 360;
            if (top + estimatedHeight > window.innerHeight - 8) {
                const above = rect.top - POPOVER_GAP - estimatedHeight;
                if (above >= 8) {
                    top = above;
                }
            }
            this.popoverStyle = {
                top: `${Math.round(top)}px`,
                left: `${Math.round(left)}px`,
                width: `${Math.round(width)}px`,
            };
        },
        async loadQuotas() {
            // 付费档等：无需打额度接口，直接展示全部线索类型为无限
            if (this.unlimited) {
                this.loadedUnlimited = true;
                this.rows = this.typeDefs.map((def) => ({ ...def, remaining: 0, limit: 0 }));
                this.$nextTick(() => {
                    if (this.open) this.updatePopoverPosition();
                });
                return;
            }
            const companyId = this.companyId;
            if (!companyId) {
                this.rows = [];
                this.loadedUnlimited = false;
                return;
            }
            const results = await Promise.all(
                this.typeDefs.map(async (def) => {
                    try {
                        const res = await InquiryFormService.GetUnlockQuotaFree({
                            companyId,
                            type: def.type,
                        });
                        const q = pickYearQuota(res);
                        return { ...def, ...q };
                    } catch (e) {
                        return { ...def, remaining: 0, limit: 0 };
                    }
                })
            );
            // limit < 0：付费/过渡无限；limit === 0：免费展台等「有额度体系但为 0」，不能当无限
            this.loadedUnlimited = results.length > 0 && results.every((r) => Number(r.limit) < 0);
            this.rows = results;
            this.$nextTick(() => {
                if (this.open) this.updatePopoverPosition();
            });
        },
    },
};
</script>

<style lang="scss" scoped>
.sl-quota-details {
    position: relative;
    flex-shrink: 0;

    &--link .sl-quota-details__trigger {
        color: #7968FF;
        padding: 0;
    }

    &--link .sl-quota-details__trigger-text {
        font-size: 14px;
        font-weight: 600;
        line-height: 130%;
        color: #7968FF;
    }

    &--link .sl-quota-details__trigger-chevron {
        color: #7968FF;
    }
}

.sl-quota-details__trigger {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 0 4px;
    border: none;
    background: transparent;
    cursor: pointer;
    color: rgba(0, 0, 0, 0.6);
    line-height: 1;
}

.sl-quota-details__trigger-text {
    font-size: 12px;
    font-weight: 600;
    line-height: 16px;
    white-space: nowrap;
}

.sl-quota-details__trigger-affix {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    flex-shrink: 0;
}

.sl-quota-details__trigger-icon {
    display: block;
    width: 15px;
    height: 15px;
    margin: 0;
    padding: 0;
    flex-shrink: 0;
}

.sl-quota-details__trigger-chevron {
    display: block;
    width: 16px;
    height: 16px;
    color: rgba(0, 0, 0, 0.6);
}

.sl-quota-details__trigger-chevron--down {
    transform: rotate(180deg);
}
</style>

<!-- popover 挂到 body 后 scoped 样式失效，用非 scoped 全局类 -->
<style lang="scss">
.sl-quota-popover {
    position: fixed;
    z-index: 4000;
    display: flex;
    flex-direction: column;
    max-width: 300px;
    border-radius: 12px;
    background: #fff;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
    overflow: hidden;
    box-sizing: border-box;
}

.sl-quota-popover__top {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 16px;
}

.sl-quota-popover__row {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 8px;
}

.sl-quota-popover__title {
    color: rgba(0, 0, 0, 0.8);
    font-size: 16px;
    font-weight: 700;
    line-height: 130%;
}

.sl-quota-popover__nums {
    color: rgba(0, 0, 0, 0.8);
    font-size: 14px;
    font-weight: 700;
    line-height: 130%;
    white-space: nowrap;

    strong {
        font-size: 20px;
        font-weight: 700;
        line-height: 130%;
    }
}

.sl-quota-popover__nums--unlimited {
    font-weight: 400;
    color: rgba(0, 0, 0, 0.6);
}

.sl-quota-popover__nums-sub {
    margin-left: 4px;
    font-weight: 400;
    color: rgba(0, 0, 0, 0.6);
}

.sl-quota-popover__desc {
    margin: 0;
    color: rgba(0, 0, 0, 0.8); /* #00000080 */
    font-size: 12px;
    font-weight: 400;
    line-height: 150%;
}

.sl-quota-popover__list {
    list-style: none;
    margin: 0;
    padding: 12px 16px 16px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    border-top: 1px solid rgba(0, 0, 0, 0.08);
}

.sl-quota-popover__desc strong {
    font-weight: 700;
    color: rgba(0, 0, 0, 0.8);
}

.sl-quota-popover__wall-link {
    color: #7366FF;
    font-weight: 600;
    text-decoration: underline;
    cursor: pointer;
}

.sl-quota-popover__wall-link:hover {
    color: #5b4ee0;
}

.sl-quota-popover__item {
    display: flex;
    align-items: center;
    gap: 8px;
}

.sl-quota-popover__item-icon {
    flex-shrink: 0;
    opacity: 0.55;
}

.sl-quota-popover__item-label {
    flex: 1 1 auto;
    min-width: 0;
    color: rgba(0, 0, 0, 0.8);
    font-size: 13px;
    font-weight: 500;
    line-height: 130%;
}

.sl-quota-popover__item-nums {
    flex-shrink: 0;
    color: rgba(0, 0, 0, 0.6);
    font-size: 13px;
    font-weight: 600;
    line-height: 130%;
    white-space: nowrap;
}

.sl-quota-popover__item-nums--unlimited {
    font-weight: 400;
}

.sl-quota-popover__upgrade {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-end;
    gap: 12px;
    padding: 16px;
    /* 设计稿：右下角黄橙高光 → 紫靛纵深 */
    background: radial-gradient(
        ellipse 241.8% 251.52% at 102.83% 124.79%,
        #FFD631 0%,
        #FF7931 14%,
        #9554E2 45%,
        #8550FF 75%,
        #6231FF 100%
    );
}

.sl-quota-popover__upgrade-text {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    align-self: stretch;
    gap: 4px;
}

.sl-quota-popover__upgrade-title {
    color: #fff;
    font-size: 14px;
    font-weight: 700;
    line-height: 130%;
}

.sl-quota-popover__upgrade-desc {
    margin: 0;
    align-self: stretch;
    opacity: 0.9;
    color: #fff;
    font-size: 12px;
    font-weight: 400;
    line-height: 130%;
}

.sl-quota-popover__upgrade-actions {
    display: inline-flex;
    align-items: center;
    justify-content: flex-start;
    gap: 10px;
}

.sl-quota-popover__btn-learn {
    color: #fff !important;
    font-size: 12px;
    font-weight: 600;
    line-height: 12px;
    text-decoration: none !important;
    white-space: nowrap;
}

.sl-quota-popover__btn-sales {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 12px;
    border-radius: 8px;
    background: #fff;
    color: #7366FF !important;
    font-size: 12px;
    font-weight: 600;
    line-height: 12px;
    text-decoration: none !important;
    white-space: nowrap;
}
</style>
